import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import {
  CreateTeamMeetingDto,
  FetchError,
  GetMeetingsForTeamDto,
  MeetingAgendaItemDto,
  TeamMeetingDto,
  UserDto,
} from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";
import { optimisticallyUpdateQueryData } from "../utilities";
import { useSignalR } from "../../../hooks";

type Variables = {
  teamGuid: string;
  meetingGuid: string;
  title: string;
  description: string;
  externalCallUrl?: string;
  invitees: UserDto[];
  agendaItems: MeetingAgendaItemDto[];
};

const useCreateTeamMeetingRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function createTeamMeetingRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();
    const body: CreateTeamMeetingDto = {
      meetingGuid: variables.meetingGuid,
      title: variables.title,
      description: variables.description,
      externalCallUrl: variables.externalCallUrl,
      inviteeUserIds: variables.invitees.map((user) => user.userId),
      agendaItems: variables.agendaItems,
    };

    const response = await fetch(`${configuration.apiRootUrl}/teams/${variables.teamGuid}/meetings`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "post",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { createTeamMeetingRequest };
};

const useCreateTeamMeeting = () => {
  const { createTeamMeetingRequest } = useCreateTeamMeetingRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: createTeamMeetingRequest,
    onMutate: async (variables) => {
      const newMeeting: TeamMeetingDto = {
        guid: variables.meetingGuid,
        title: variables.title,
        description: variables.description,
        isInvited: true,
        canEdit: true,
        currentInstance: null,
        instancesCount: 0,
        lastInstanceDateTimeUtc: null,
      };

      let cachedTeamData = await optimisticallyUpdateQueryData<GetMeetingsForTeamDto>(
        queryClient,
        queryKeys.meetings.getMeetingsForTeam(variables.teamGuid),
        (cachedData) => ({
          meetings: [...cachedData.meetings, newMeeting],
        })
      );

      return { cachedTeamData };
    },
    onSuccess: (_data, variables) => {
      signalR.sendRefetchTeamMeetingsList(variables.teamGuid);
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(queryKeys.meetings.getMeetingsForTeam(variables.teamGuid), context?.cachedTeamData);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.meetings.getMeetingsForTeam(variables.teamGuid));
      queryClient.invalidateQueries(queryKeys.meetings.getMeetingsForTeam(variables.teamGuid));
    },
  });
};

export { useCreateTeamMeeting };
